class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.handleDrawerScrollLock = this.handleDrawerScrollLock.bind(this);
    this.closeVersion = 0;
    this.openTimer = null;
    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) window.ssCartDrawerSuppressAutoOpenUntil = 0;
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    clearTimeout(this.openTimer);
    this.openTimer = setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
    document.body.classList.add('ss-cart-drawer-open');
    document.documentElement.classList.add('ss-cart-drawer-open');
    document.addEventListener('touchmove', this.handleDrawerScrollLock, { passive: false, capture: true });
    document.addEventListener('wheel', this.handleDrawerScrollLock, { passive: false, capture: true });
  }

  close() {
    this.closeVersion += 1;
    window.ssCartDrawerSuppressAutoOpenUntil = Date.now() + 6000;
    clearTimeout(this.openTimer);
    this.openTimer = null;
    window.ssCloseBundleOffer?.();
    this.cleanupDrawerState();
    removeTrapFocus(this.activeElement);
  }

  cleanupDrawerState() {
    this.classList.remove('active', 'animate');
    document.body.classList.remove('overflow-hidden');
    document.body.classList.remove('ss-cart-drawer-open');
    document.body.classList.remove('ss-bundle-offer-open');
    document.documentElement.classList.remove('ss-cart-drawer-open');
    document.documentElement.classList.remove('ss-bundle-offer-open');
    document.querySelectorAll('[data-ss-bundle-offer]').forEach((offer) => {
      offer.hidden = true;
      offer.setAttribute('aria-hidden', 'true');
    });
    document.removeEventListener('touchmove', this.handleDrawerScrollLock, { capture: true });
    document.removeEventListener('wheel', this.handleDrawerScrollLock, { capture: true });
  }

  handleDrawerScrollLock(event) {
    if (!document.body.classList.contains('ss-cart-drawer-open')) return;
    if (event.target.closest('.ss-bundle-offer:not([hidden])')) return;

    const scrollableCartItems = event.target.closest('cart-drawer-items');
    if (scrollableCartItems && this.contains(scrollableCartItems)) return;

    event.preventDefault();
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    const isAutoOpenSuppressed = Date.now() < Number(window.ssCartDrawerSuppressAutoOpenUntil || 0);
    const shouldOpenDrawer = parsedState.openDrawer !== false && !isAutoOpenSuppressed;
    const closeVersion = this.closeVersion;
    const incomingDrawer = parsedState.sections?.['cart-drawer']
      ? this.getSectionDOM(parsedState.sections['cart-drawer'], 'cart-drawer')
      : null;
    const isEmpty =
      parsedState.item_count === 0 ||
      parsedState.items?.length === 0 ||
      incomingDrawer?.classList.contains('is-empty');

    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    this.classList.toggle('is-empty', Boolean(isEmpty));
    this.querySelector('.drawer__inner')?.classList.toggle('is-empty', Boolean(isEmpty));

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      if (!shouldOpenDrawer || closeVersion !== this.closeVersion) {
        this.cleanupDrawerState();
        return;
      }
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
